import { useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button, Divider, CustomTooltip } from "../shared";
import {
  HiOutlineUserPlus,
  HiOutlinePencilSquare,
  HiOutlineFolder,
  HiOutlineCog,
} from "react-icons/hi2";
import { ProjectsIcon } from "../../assets/icons";
import EditTeamModal from "./EditTeamModal";
import InviteTeamMemberModal from "./InviteTeamMemberModal";

const SECTION_LABELS = {
  overview: "Overview",
  members: "Members",
  invites: "Invites",
  storage: "Files",
  settings: "Settings",
};

const tabs = [
  { key: "overview", label: "Overview", path: "" },
  { key: "members", label: "Members", path: "/members" },
  { key: "invites", label: "Invites", path: "/invites" },
  { key: "storage", label: "Files", path: "/storage" },
  { key: "settings", label: "Settings", path: "/settings" },
];

const TeamWorkspaceHeader = ({ team, canManageTeam, teamId, activeSection = "overview", projects = [] }) => {
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const visibleTabs = useMemo(() => {
    if (team?.is_personal) {
      return tabs.filter((tab) => tab.key !== "invites");
    }
    return tabs;
  }, [team?.is_personal]);

  return (
    <>
      <div className="bg-bggrey pt-4 sticky top-0 z-50 rounded-b-xl"
      >
        <div className="bg-white rounded-xl"
        >
          <div className="flex flex-col sm:flex-row w-full justify-between items-center gap-4 sm:gap-0 px-4 py-3"
          >
            <div className="font-sans flex items-center gap-3 w-full sm:w-auto"
            >
              <ProjectsIcon width={30} height={30} className="text-black flex-shrink-0" />
              <div className="flex items-center gap-2 min-w-0"
              >
                <Link
                  to="/teams"
                  className="text-sm text-grey hover:text-secondary-dark transition"
                >
                  Teams
                </Link>
                <span className="text-grey">/</span>
                <CustomTooltip content={team?.name || "—"}>
                  <span className="text-sm font-semibold max-w-[150px] truncate inline-block align-bottom">
                    {team?.name || "—"}
                  </span>
                </CustomTooltip>
                <span className="text-grey">/</span>
                <span className="text-sm">{SECTION_LABELS[activeSection] || "Overview"}</span>
              </div>
            </div>
            {canManageTeam && (
              <div className="font-sans grid grid-cols-2 w-full gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-end"
              >
                <Button
                  onClick={() => setEditModalOpen(true)}
                  variant="outlined"
                  color="secondary"
                  size="md"
                  icon={HiOutlinePencilSquare}
                  className="gap-2 w-full sm:w-auto justify-center"
                >
                  {team?.is_personal ? "Edit workspace" : "Edit team"}
                </Button>
                {!team?.is_personal && (
                  <Button
                    onClick={() => setInviteModalOpen(true)}
                    variant="outlined"
                    color="secondary"
                    size="md"
                    icon={HiOutlineUserPlus}
                    className="gap-2 w-full sm:w-auto justify-center"
                  >
                    Add Team Members
                  </Button>
                )}
                <Button
                  onClick={() => navigate(`/teams/${teamId}/storage`)}
                  variant="outlined"
                  color="secondary"
                  size="md"
                  icon={HiOutlineFolder}
                  className="gap-2 w-full sm:w-auto justify-center"
                >
                  Files
                </Button>
                <Button
                  onClick={() => navigate(`/teams/${teamId}/settings`)}
                  variant="outlined"
                  color="secondary"
                  size="md"
                  icon={HiOutlineCog}
                  className="gap-2 w-full sm:w-auto justify-center"
                >
                  Settings
                </Button>
              </div>
            )}
          </div>
          <div className="px-2">
            <Divider />
          </div>
          <nav className="flex flex-wrap gap-x-1 gap-y-1 px-2 pb-1"
          >
            {visibleTabs.map((tab) => {
              const isActive = activeSection === tab.key;
              return (
                <NavLink
                  key={tab.key}
                  to={`/teams/${teamId}${tab.path}`}
                  className={`px-4 py-2.5 text-sm font-medium transition rounded-t-lg ${
                    isActive
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-grey hover:text-darkgrey hover:bg-surface-hover"
                  }`}
                >
                  {tab.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {canManageTeam && (
        <>
          <EditTeamModal
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            teamId={teamId}
            team={team}
          />
          <InviteTeamMemberModal
            isOpen={inviteModalOpen}
            onClose={() => setInviteModalOpen(false)}
            teamId={teamId}
            projects={projects}
          />
        </>
      )}
    </>
  );
};

export default TeamWorkspaceHeader;
